import {logger} from 'yougile-util'

type DisposeHandler = () => void

export type UserActivityCallback = (isActive: boolean) => void

const defaultThreshold = 300_000 // 5 minutes in milliseconds

export class UserActivityMonitor {
  private disposeCallback: DisposeHandler | undefined
  public constructor(disposeCallback: DisposeHandler) {
    this.disposeCallback = disposeCallback
  }

  /**
   * Checks if the user is active based on the last activity time.
   * @param threshold - The time in milliseconds to consider the user as active. Default is 5 minutes (300000 ms).
   */
  public isActive(threshold = defaultThreshold): boolean {
    const tracker = UserActivityTracker.instance
    if (tracker) {
      return tracker.isActive(threshold)
    }
    return false
  }

  /**
   * Registers a callback to watch for user activity changes for a given threshold.
   * This method will call the handler immediately with the current activity state and then
   * will call it again whenever the activity state changes every `threshold` milliseconds.
   * @param handler
   * @param threshold
   */
  public trackActivity(
    handler: UserActivityCallback,
    threshold = defaultThreshold
  ): (() => void) | undefined {
    const tracker = UserActivityTracker.instance
    if (tracker) {
      return tracker.trackActivity(handler, threshold)
    }
    logger.warn(
      '[UserActivityMonitor] No active UserActivityTracker instance found.'
    )
    return undefined
  }

  public dispose(): void {
    if (this.disposeCallback) {
      this.disposeCallback()
      this.disposeCallback = undefined
    }
  }
}

export class UserActivityTracker {
  public static instance: UserActivityTracker | undefined
  public static acquireRequests: string[] = []
  private lastActivityTime: number
  private disposeHandlers: DisposeHandler[] = []
  private watchers: UserActivityCallback[] = []

  public static acquire(): UserActivityMonitor {
    const randomId = Math.random().toString(36).substring(2, 15)
    UserActivityTracker.acquireRequests.push(randomId)

    if (UserActivityTracker.acquireRequests.length === 1) {
      if (!UserActivityTracker.instance) {
        UserActivityTracker.instance = new UserActivityTracker()
      } else {
        logger.error('[UserActivityTracker] Instance already exists.')
      }
    }

    return new UserActivityMonitor(() => {
      const index = UserActivityTracker.acquireRequests.indexOf(randomId)
      if (index !== -1) {
        UserActivityTracker.acquireRequests.splice(index, 1)
      }
      if (UserActivityTracker.instance) {
        UserActivityTracker.instance.dispose()
        UserActivityTracker.instance = undefined
      }
    })
  }

  constructor() {
    this.lastActivityTime = Date.now()
    this.initialize()
  }

  private initialize(): void {
    const activityHandler = () => this.updateLastActivityTime()

    document.addEventListener('mousemove', activityHandler)
    document.addEventListener('keydown', activityHandler)
    document.addEventListener('click', activityHandler)

    this.disposeHandlers.push(() => {
      document.removeEventListener('mousemove', activityHandler)
      document.removeEventListener('keydown', activityHandler)
      document.removeEventListener('click', activityHandler)
    })
  }

  private updateLastActivityTime(): void {
    this.lastActivityTime = Date.now()
    this.watchers.forEach(watcher => {
      try {
        watcher(this.isActive())
      } catch (error) {
        logger.error('[UserActivityTracker] Error in watcher callback:', error)
      }
    })
  }

  public getLastActivityTime(): number {
    return this.lastActivityTime
  }

  /**
   * Checks if the user is active based on the last activity time.
   * @param threshold - The time in milliseconds to consider the user as active. Default is 5 minutes (300000 ms).
   */
  public isActive(threshold = defaultThreshold): boolean {
    return Date.now() - this.lastActivityTime < threshold
  }

  /**
   * Registers a callback to watch for user activity changes for a given threshold.
   * @param handler
   * @param threshold - The time in milliseconds to consider the user as active. Default is 5 minutes (300000 ms).
   */
  public trackActivity(
    handler: UserActivityCallback,
    threshold = defaultThreshold
  ): DisposeHandler {
    let lastCheckTime = Date.now()
    const internalHandler = () => {
      const nextActive = this.isActive(threshold)
      const nextTime = Date.now()
      if (nextTime - lastCheckTime >= threshold) {
        lastCheckTime = nextTime
        handler(nextActive)
      }
    }

    this.watchers.push(internalHandler)

    handler(this.isActive(threshold))

    return () => {
      const index = this.watchers.indexOf(internalHandler)
      if (index !== -1) {
        this.watchers.splice(index, 1)
      }
    }
  }

  public dispose() {
    this.disposeHandlers.forEach(handler => handler())
  }
}
